import { message } from 'antd';

// API 错误类型
export enum ApiErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTH_ERROR = 'AUTH_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

// API 错误类
export class ApiError extends Error {
  public type: ApiErrorType;
  public status?: number;
  public code?: string;

  constructor(
    message: string,
    type: ApiErrorType = ApiErrorType.UNKNOWN_ERROR,
    status?: number,
    code?: string
  ) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.code = code;
  }
}

// 错误处理工具类
export class ErrorHandler {
  // 处理 API 错误
  static handleApiError(error: any, showMessage: boolean = true): ApiError {
    let apiError: ApiError;

    if (error instanceof ApiError) {
      apiError = error;
    } else if (error.name === 'TypeError' && error.message.includes('fetch')) {
      apiError = new ApiError(
        '网络连接失败，请检查网络设置',
        ApiErrorType.NETWORK_ERROR
      );
    } else if (error.status === 401) {
      apiError = new ApiError(
        '登录已过期，请重新登录',
        ApiErrorType.AUTH_ERROR,
        error.status
      );
      // 清除本地存储的 token
      localStorage.removeItem('auth_token');
      // 可以在这里添加重定向到登录页的逻辑
    } else if (error.status === 403) {
      apiError = new ApiError(
        '没有权限执行此操作',
        ApiErrorType.AUTH_ERROR,
        error.status
      );
    } else if (error.status === 422) {
      apiError = new ApiError(
        error.message || '数据验证失败',
        ApiErrorType.VALIDATION_ERROR,
        error.status
      );
    } else if (error.status >= 500) {
      apiError = new ApiError(
        '服务器内部错误，请稍后重试',
        ApiErrorType.SERVER_ERROR,
        error.status
      );
    } else {
      apiError = new ApiError(
        error.message || '未知错误',
        ApiErrorType.UNKNOWN_ERROR,
        error.status
      );
    }

    // 记录错误日志
    console.error('API Error:', {
      type: apiError.type,
      message: apiError.message,
      status: apiError.status,
      code: apiError.code,
      originalError: error,
    });

    // 显示错误消息
    if (showMessage) {
      this.showErrorMessage(apiError);
    }

    return apiError;
  }

  // 显示错误消息
  static showErrorMessage(error: ApiError): void {
    switch (error.type) {
      case ApiErrorType.NETWORK_ERROR:
        message.error(error.message);
        break;
      case ApiErrorType.AUTH_ERROR:
        message.error(error.message);
        break;
      case ApiErrorType.VALIDATION_ERROR:
        message.warning(error.message);
        break;
      case ApiErrorType.SERVER_ERROR:
        message.error(error.message);
        break;
      default:
        message.error(error.message);
    }
  }

  // 创建网络错误
  static createNetworkError(): ApiError {
    return new ApiError(
      '网络连接失败，请检查网络设置',
      ApiErrorType.NETWORK_ERROR
    );
  }

  // 创建认证错误
  static createAuthError(message: string = '认证失败'): ApiError {
    return new ApiError(message, ApiErrorType.AUTH_ERROR, 401);
  }

  // 创建验证错误
  static createValidationError(message: string): ApiError {
    return new ApiError(message, ApiErrorType.VALIDATION_ERROR, 422);
  }

  // 创建服务器错误
  static createServerError(message: string = '服务器错误'): ApiError {
    return new ApiError(message, ApiErrorType.SERVER_ERROR, 500);
  }
}

// 导出默认实例
export default ErrorHandler;
